import React, { useCallback, useEffect, useState } from 'react';
import AuthView from './AuthView';
import { useTranslation } from '../i18n/useTranslation';
import {
  isAuthReauthRequired,
  markAuthHealthy,
  subscribeAuthHealth,
} from '../lib/authHealth';
import { shouldDeclawInvoluntaryLogout } from '../lib/authDeclaw';

/**
 * Full-screen blocking re-auth panel when connector reports sign-in family burned.
 * Not dismissible — reuses AuthView (no forked login flow).
 */
export default function AuthReauthRequiredPanel() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(
    () => isAuthReauthRequired() && !shouldDeclawInvoluntaryLogout(),
  );

  const syncVisible = useCallback(() => {
    setVisible(isAuthReauthRequired() && !shouldDeclawInvoluntaryLogout());
  }, []);

  useEffect(() => subscribeAuthHealth(syncVisible), [syncVisible]);

  const handleAuthenticated = useCallback(() => {
    markAuthHealthy();
    syncVisible();
  }, [syncVisible]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-reauth-required-title"
      data-testid="auth-reauth-required-panel"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100100,
        background: '#f8f8f8',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          textAlign: 'center',
          padding: '32px 24px 8px',
          maxWidth: 520,
          margin: '0 auto',
          width: '100%',
        }}
      >
        <h1
          id="auth-reauth-required-title"
          style={{
            margin: '0 0 12px',
            fontSize: 24,
            fontWeight: 700,
            color: '#111',
            letterSpacing: '-0.3px',
          }}
        >
          {t('auth.reauthRequired.title')}
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: 15,
            lineHeight: 1.6,
            color: '#555',
          }}
        >
          {t('auth.reauthRequired.body')}
        </p>
      </div>
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>
        <AuthView isModal onAuthenticated={handleAuthenticated} />
      </div>
    </div>
  );
}
